/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { Modal, Form, Button, Tabs, Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import '../../scss/frontend/consent-modal.scss';
import { SlArrowRight, SlArrowLeft } from 'react-icons/sl';

import {
  TransactionType,
  ConsentAgreementFormDataType,
} from '../../model/consent.data-type';

import {
  GetUserConsent,
  GetActivePurposes,
  SetUserConsentAgreement,
  GetOneTrustPurposesById
} from '../../services/ApiConsent';

import { useUser } from '../../context/UserContext';

export const ConsentModal = (props: any) => {
  const { t, i18n } = useTranslation();
  const [activePurposes, setActivePurposes] = useState<any[] | []>([]);
  const [isFormUpdated, setIsFormUpdated] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [tabKey, setTabKey] = useState(1);
  
  // Using UserContext instead of direct API call
  const { userInfo } = useUser();

  useEffect(() => {
    (async () => {
      try {
        if (userInfo) {
          let activePurposesRes = await GetOneTrustPurposesById();
          const userPurposesAgreement = await GetUserConsent({
            idOrEmail: userInfo?.email,
          });

          if (userPurposesAgreement?.content?.length) {
            setIsUpdate(true);
            const userPurposes = userPurposesAgreement?.content[0]?.Purposes;

            // merge OneTrust response with user agreement
            activePurposesRes = activePurposesRes?.map((p: any) => {


              const isExisting = userPurposes?.find(
                (up: any) => up.Id === p.purposeId,
              );

              let userAgreement = TransactionType.new;

              if (isExisting) {
                if (isExisting.Status === 'ACTIVE') {
                  userAgreement = TransactionType.accept;
                } else {
                  userAgreement = TransactionType.decline;
                }
              }

              if (p?.version !== isExisting?.Version) {
                userAgreement = TransactionType.new;
              }

              return {
                ...p,
                userAgreement: userAgreement,
              };
            });
          } else {
            setIsFormUpdated(true);
          }

          if (activePurposesRes) {
            // sort purposes
            activePurposesRes = activePurposesRes?.sort(
              (a: any, b: any) => a.sort - b.sort,
            );

            setActivePurposes(activePurposesRes);
          }
        }
      } catch (error) {
        console.log('error', error);
      }
    })();
  }, [props?.showModal]);

  const isThLang = localStorage.getItem('Language') === 'th';
  const switchLang = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('Language', lang);
  };

  const { register, handleSubmit, watch } = useForm();
  const onSubmit = ({ purposes }: any) => {
    (async () => {
      try {
        const formData: ConsentAgreementFormDataType = {
          identifier: userInfo?.email || '', // Adding fallback empty string to satisfy type check
          purposes: purposes,
          dsDataElements: {
            // Split employeeNameEn to use as first/last name
            FirstName: userInfo?.employeeNameEn?.split(' ')[0] || '',
            LastName: userInfo?.employeeNameEn?.split(' ')[1] || '',
          },
        };

        const response = await SetUserConsentAgreement(formData);

        if (response?.receipt) {
          props?.hideModal();
        }
      } catch (error) {
        toast.error('Something went wrong! Please try again later!');
      }
    })();
  };

  const watchForm = watch();

  // show hide submit button
  useEffect(() => {
    if (watchForm?.purposes?.length && isUpdate) {
      const wps = watchForm?.purposes || [];

      for (const wp of wps) {
        const currentPurpose: any = activePurposes?.find(
          (ap) => ap?.purposeId === wp?.Id,
        );

        const updated =
          currentPurpose?.userAgreement !== wp?.TransactionType ? true : false;

        setIsFormUpdated(updated);

        if (updated) {
          break;
        }
      }
    }
  }, [watchForm]);

  if (!activePurposes?.length) return <></>;

  return (
    <Modal
      show={props?.showModal}
      onHide={props?.hideModal}
      backdrop="static"
      keyboard={false}
      className="consent"
      fullscreen={true}
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header className="modal-header" style={{ position: 'sticky', top: 0, background: 'white', zIndex: 999, padding: '1.5rem 2rem', textAlign: 'center', display: 'block', borderBottom: '1px solid rgba(0,0,0,0.1)', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
          <Modal.Title>
           <div className="lang-switch-wrapper" style={{ marginBottom: '1rem' }}>
                <div style={{ 
                  display: 'inline-flex', 
                  background: '#f8f9fa', 
                  borderRadius: '25px', 
                  padding: '4px',
                  border: '1px solid #e9ecef',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <Button
                    variant="outline-secondary"
                    className={`lang-switch-btn ${isThLang ? 'active' : ''}`}
                    onClick={() => switchLang('th')}
                    style={{
                      borderRadius: '20px',
                      padding: '8px 20px',
                      margin: '0 2px',
                      border: 'none',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      minWidth: '50px',
                      transition: 'all 0.3s ease',
                      backgroundColor: isThLang ? '#6c63ff' : 'transparent',
                      color: isThLang ? 'white' : '#6c757d',
                      boxShadow: isThLang ? '0 2px 8px rgba(108, 99, 255, 0.3)' : 'none'
                    }}
                  >
                    ไทย
                  </Button>
                  <Button
                    variant="outline-secondary"
                    className={`lang-switch-btn ${!isThLang ? 'active' : ''}`}
                    onClick={() => switchLang('en')}
                    style={{
                      borderRadius: '20px',
                      padding: '8px 20px',
                      margin: '0 2px',
                      border: 'none',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      minWidth: '50px',
                      transition: 'all 0.3s ease',
                      backgroundColor: !isThLang ? '#6c63ff' : 'transparent',
                      color: !isThLang ? 'white' : '#6c757d',
                      boxShadow: !isThLang ? '0 2px 8px rgba(108, 99, 255, 0.3)' : 'none'
                    }}
                  >
                    ENG
                  </Button>
                </div>
              </div>
              <span className="title" style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#2c3e50',
                display: 'block',
                marginTop: '0.5rem'
              }}>
                {t('consent.user_agreement.modal_title')}
              </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 consent-modal-body">
          <Tabs defaultActiveKey={1} activeKey={tabKey} fill>
            <Tab eventKey={1} title="" tabClassName="d-none">
              <div className="description">
                {isThLang ? (
                  <iframe
                    title="Privacy Notice"
                    src="/one-trust/privacy-notice-th.html"
                  ></iframe>
                ) : (
                  <iframe
                    title="Privacy Notice"
                    src="/one-trust/privacy-notice-en.html"
                  ></iframe>
                )}
              </div>
            </Tab>
            <Tab eventKey={2} title="" tabClassName="d-none">
              <div className="purposes">
                {activePurposes?.map((activePurpose, i) => (
                  <div className="purpose" key={activePurpose?.purposeId}>
                    <input
                      type="hidden"
                      value={activePurpose?.purposeId}
                      {...register(`purposes.${i}.Id`)}
                    />
                    <input
                      type="hidden"
                      value={activePurpose?.version}
                      {...register(`purposes.${i}.Version`)}
                    />
                    <div className="purpose-content">
                      <h3 className="purpose-title">
                        {(i18n.language === 'th') ? activePurpose?.labelTh : activePurpose?.labelEn}
                      </h3>
                      <p className="purpose-description">
                        {(i18n.language === 'th') ? activePurpose?.descriptionTh : activePurpose?.descriptionEn}
                      </p>
                    </div>

                    <div className="purpose-controls">
                      <Form.Check
                        inline
                        label={t('accept')}
                        type="radio"
                        required
                        className="purpose-radio accept"
                        value={TransactionType.accept}
                        defaultChecked={activePurpose?.userAgreement === TransactionType.accept}
                        {...register(`purposes.${i}.TransactionType`)}
                      />
                      <Form.Check
                        inline
                        label={t('decline')}
                        type="radio"
                        required
                        className="purpose-radio decline"
                        value={TransactionType.decline}
                        defaultChecked={activePurpose?.userAgreement === TransactionType.decline}
                        {...register(`purposes.${i}.TransactionType`)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>

        <Modal.Footer className="modal-footer" style={{ 
          position: 'sticky', 
          bottom: 0, 
          background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,1) 20%)', 
          zIndex: 999, 
          padding: '1.5rem 2rem',
          borderTop: '1px solid rgba(0,0,0,0.1)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
        }}>
 

          {tabKey === 1 && (
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              fontWeight: 'bold'
            }}>
              <div style={{ 
                fontSize: '16px',
                color: '#495057',
                flex: '1'
              }}>
                {t('consent.user_agreement.first_tab_btn_label')}
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                {/* <Button
                  variant="outline-danger"
                  onClick={() => window.location.href = process.env.REACT_APP_PUBLIC_HR_PORTAL_URL || '/'}
                  style={{
                    borderRadius: '50px',
                    padding: '12px 24px',
                    fontWeight: 'bold',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <SlArrowLeft style={{ marginRight: '8px' }} />
                  {t('back') || 'กลับ'}
                </Button> */}
                <Button
                  variant="outline-secondary"
                  className="btn-light-green next-btn"
                  onClick={() => setTabKey(2)}
                  style={{
                    borderRadius: '50px',
                    padding: '12px 24px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px rgba(162, 177, 98, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <SlArrowRight style={{ fontSize: '18px' }} />
                </Button>
              </div>
            </div>
          )}

          {tabKey === 2 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              width: '100%',
              gap: '1rem'
            }}>
              <Button 
                variant="outline-secondary" 
                onClick={() => setTabKey(1)}
                style={{
                  borderRadius: '50px',
                  padding: '12px 24px',
                  fontWeight: 'bold',
                  minWidth: '100px',
                  transition: 'all 0.3s ease'
                }}
              >
                <SlArrowLeft style={{ marginRight: '8px' }} />
                {t('back') || 'ย้อนกลับ'}
              </Button>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <Button 
                  variant="outline-danger" 
                  onClick={props?.hideModal}
                  style={{
                    borderRadius: '50px',
                    padding: '12px 24px',
                    fontWeight: 'bold',
                    minWidth: '100px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {t('cancel') || 'ยกเลิก'}
                </Button>

                {isFormUpdated && (
                  <Button 
                    className="btn-light-green" 
                    type="submit"
                    style={{
                      borderRadius: '50px',
                      padding: '12px 32px',
                      fontWeight: 'bold',
                      minWidth: '120px',
                      boxShadow: '0 4px 12px rgba(162, 177, 98, 0.4)',
                      transition: 'all 0.3s ease',
                      fontSize: '16px'
                    }}
                  >
                    {t('submit') || 'ยืนยัน'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </Modal.Footer>
      </Form>
       {/* Responsive CSS */}
        <style dangerouslySetInnerHTML={{
          __html: `
            @media (max-width: 768px) {
              .modal-footer {
                padding: 1rem 1.5rem !important;
              }
              
              .modal-footer button {
                font-size: 14px !important;
                padding: 12px 24px !important;
                min-width: 100px !important;
              }
              
              .modal-footer div:first-child > div:first-child {
                font-size: 14px !important;
              }
            }
            
            @media (max-width: 576px) {
              .modal-footer {
                padding: 0.75rem 1rem !important;
              }
              
              .modal-footer button {
                font-size: 12px !important;
                padding: 10px 18px !important;
                min-width: 80px !important;
              }
              
              .modal-footer div:first-child > div:first-child {
                font-size: 12px !important;
              }
              
              /* Keep horizontal layout but make buttons smaller */
              .modal-footer > div > div:last-child {
                gap: 0.5rem !important;
              }
            }
            
            @media (max-width: 480px) {
              .modal-footer {
                padding: 0.5rem 0.75rem !important;
              }
              
              .modal-footer button {
                font-size: 11px !important;
                padding: 8px 16px !important;
                min-width: 70px !important;
                border-radius: 25px !important;
              }
              
              .modal-footer div:first-child > div:first-child {
                font-size: 11px !important;
              }
              
              /* Still horizontal but very compact */
              .modal-footer > div > div:last-child {
                gap: 0.25rem !important;
              }
              
              /* Hide text in back button on very small screens */
              .modal-footer button span {
                display: none !important;
              }
              
              .modal-footer button svg {
                margin: 0 !important;
              }
            }
            
            @media (max-width: 360px) {
              .modal-footer {
                padding: 0.5rem !important;
              }
              
              .modal-footer button {
                font-size: 10px !important;
                padding: 8px 12px !important;
                min-width: 60px !important;
                border-radius: 20px !important;
              }
              
              /* Tab 1: Stack vertically only on very tiny screens */
              .modal-footer > div:first-child {
                flex-direction: column !important;
                text-align: center !important;
                gap: 0.5rem !important;
              }
              
              .modal-footer > div:first-child > div:first-child {
                font-size: 10px !important;
                margin-bottom: 0.5rem !important;
              }
            }
          `
        }} />
    </Modal>
  );
};
